import { skipToken, useQuery } from '@tanstack/react-query';

import { type CampaignId } from '@brightlocal/shared';

import { apiClient } from 'shared/apiClient';
import type { ErrorReporting } from 'shared/utils';

import { REFERENCE_DATA_STALE_TIME } from '../consts';

const GET_CAMPAIGN_QUERY_KEY = 'citation-builder-get-campaign';

const getCampaignQueryKey = (campaignId: CampaignId | null) => [GET_CAMPAIGN_QUERY_KEY, campaignId];

// One reporting context for both definitions of this query (options object + hook),
// so the shared cache entry reports identically whichever one ran the fetch.
const campaignErrorReporting = {
  feature: 'citations',
  errorMessage: 'Error fetching citation builder campaign',
  method: 'apiClient.citationBuilder.getCampaign'
} satisfies ErrorReporting;

const fetchCampaign = (campaignId: CampaignId, signal?: AbortSignal) =>
  apiClient.citationBuilder.getCampaign({ campaignId }, { signal });

/**
 * Query options for `GET /manage/v1/citation-builder/{id}`. Shared with the wizard's route
 * guard so the guard and the wizard resolve from one cache entry rather than two requests.
 *
 * @param campaignId - The campaign to fetch.
 */
export const campaignQuery = (campaignId: CampaignId) => ({
  queryKey: getCampaignQueryKey(campaignId),
  meta: { errorReporting: campaignErrorReporting },
  queryFn: ({ signal }: { signal?: AbortSignal }) => fetchCampaign(campaignId, signal),
  staleTime: REFERENCE_DATA_STALE_TIME
});

/**
 * Fetches a campaign's saved order and projects the primary campaign
 * (`campaigns[0]`) — the flow hydrates the wizard store from it on entry. `select`
 * keeps the take-first projection in the feature rather than the api-client schema,
 * and yields `undefined` when the order carries no campaign yet.
 */
export const useGetCampaign = (campaignId: CampaignId | null) =>
  useQuery({
    queryKey: getCampaignQueryKey(campaignId),
    meta: { errorReporting: campaignErrorReporting },
    queryFn: campaignId !== null ? ({ signal }) => fetchCampaign(campaignId, signal) : skipToken,
    staleTime: REFERENCE_DATA_STALE_TIME,
    select: (data) => data.campaigns[0]
  });
